<template>
  <TopLevelPageShell title="Inventory">
    <template #subtitle>
      <span class="inventory-subtitle">
        List and manage active vehicles in your current dealer inventory.
        <br />
        You can add vehicles manually or import via your feed sources. Archived vehicles are managed separately.
      </span>
    </template>

    <template #actions>
      <v-btn
        variant="outlined"
        color="primary"
        prepend-icon="mdi-archive-outline"
        @click="navigateToArchivedInventory"
      >
        View Archived Inventory
      </v-btn>
      <v-btn
        v-if="!mdAndDown"
        color="primary"
        variant="tonal"
        prepend-icon="mdi-plus"
        @click="navigateToAddVehicle"
      >
        Add Vehicle
      </v-btn>
    </template>

    <template #preDivider>
      <v-alert
        v-if="unpublishedSummaryText"
        type="info"
        variant="tonal"
        density="comfortable"
        class="mt-4 mb-0"
      >
        <div class="d-flex align-center justify-space-between flex-wrap ga-2">
          <span>{{ unpublishedSummaryText }}</span>
          <v-btn
            size="small"
            variant="tonal"
            color="primary"
            class="text-none"
            @click="goToUnpublishedChanges"
          >
            View unpublished changes
          </v-btn>
        </div>
      </v-alert>
    </template>

        <div class="text-caption text-grey-darken-1 mb-6" style="font-style: italic">
          💡 Tip: Search by VIN, make/model, or stock # to find vehicles instantly.
        </div>

        <!-- Controls Bar -->
        <v-sheet class="control-bar pa-3 mb-4" rounded>
          <InventorySearchToolbarRow>
            <v-text-field
              v-model="searchQuery"
              placeholder="Search VIN, make, model, or stock #"
              density="comfortable"
              variant="outlined"
              hide-details
              prepend-inner-icon="mdi-magnify"
              class="control-input"
              @input="handleSearchInput"
              @keyup.enter="triggerSearchNow"
            >
              <template #append-inner>
                <v-btn
                  color="primary"
                  size="small"
                  variant="tonal"
                  class="ml-2"
                  :loading="isLoading"
                  @click="triggerSearchNow"
                >
                  Search
                </v-btn>
              </template>
            </v-text-field>
          </InventorySearchToolbarRow>

          <v-row class="mt-3" no-gutters>
            <v-col cols="12" sm="6" md="4" lg="2" class="pr-sm-3 mb-3">
              <v-select
                v-model="inventoryStateFilter"
                :items="inventoryStateOptions"
                item-title="label"
                item-value="value"
                density="comfortable"
                variant="outlined"
                hide-details
                prepend-inner-icon="mdi-filter-variant"
                label="Inventory State"
              />
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2" class="pr-md-3 mb-3">
              <v-select
                v-model="feedTypeFilter"
                :items="sourceOptions"
                item-title="label"
                item-value="value"
                density="comfortable"
                variant="outlined"
                hide-details
                prepend-inner-icon="mdi-filter-variant"
                label="Source"
              />
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2" class="pr-sm-3 mb-3">
              <v-select
                v-model="groupFilter"
                :items="groupOptions"
                item-title="label"
                item-value="value"
                item-props="props"
                density="comfortable"
                variant="outlined"
                hide-details
                prepend-inner-icon="mdi-filter-variant"
                label="Group"
              />
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2" class="pr-md-3 mb-3">
              <v-select
                v-model="inventoryUsageTypeFilter"
                :items="inventoryUsageTypeOptions"
                item-title="label"
                item-value="value"
                density="comfortable"
                variant="outlined"
                hide-details
                prepend-inner-icon="mdi-filter-variant"
                label="Usage Type"
              />
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2" class="pr-sm-3 mb-3">
              <v-select
                v-model="websiteVisibilityFilter"
                :items="websiteVisibilityOptions"
                item-title="label"
                item-value="value"
                density="comfortable"
                variant="outlined"
                hide-details
                prepend-inner-icon="mdi-web"
                label="On Site"
              />
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2" class="mb-3">
              <v-select
                v-model="tagFilter"
                :items="allTagOptions"
                item-title="label"
                item-value="value"
                density="comfortable"
                variant="outlined"
                hide-details
                clearable
                prepend-inner-icon="mdi-tag-outline"
                label="Tag"
              />
            </v-col>
          </v-row>

          <v-row no-gutters class="align-center">
            <v-col cols="12" sm="8" class="text-caption text-grey-darken-1">
              Archived vehicles are managed separately from current inventory.
            </v-col>
            <v-col cols="12" sm="4" class="d-flex justify-start justify-sm-end mt-2 mt-sm-0">
              <v-btn
                variant="text"
                color="primary"
                @click="clearFilters"
                :disabled="isLoading || !hasActiveFilters"
              >
                Reset Filters
              </v-btn>
            </v-col>
          </v-row>
        </v-sheet>

        <!-- Total count (below filters, above table) -->
        <div class="text-caption text-grey-darken-1 mb-3" v-if="!isLoading && totalItems > 0">
          Showing {{ tableItems.length }} of {{ totalItems }} vehicles
        </div>

        <v-data-table-server
          :headers="computedHeaders"
          :items="tableItems"
          :items-length="totalItems"
          :loading="isLoading"
          :page="tableOptions.page"
          :items-per-page="tableOptions.itemsPerPage"
          :sort-by="tableOptions.sortBy"
          class="elevation-1 inventory-table"
          hover
          @update:options="updateOptions"
          @click:row="handleRowClick"
        >
          <template #header.vehicle="{ column, toggleSort, sortBy }">
            <div class="d-flex align-center flex-wrap ga-2">
              <span>{{ column.title }}</span>
              <InventoryVehicleSortControls
                :sort-by="sortBy"
                @toggle="(key) => toggleSort({ ...column, key })"
              />
            </div>
          </template>

          <template #header.visibility="{ column }">
            <div class="d-flex align-center flex-wrap ga-2">
              <span>{{ column.title }}</span>
              <v-menu open-on-hover open-on-click location="bottom" max-width="400">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-information-outline"
                    variant="text"
                    size="x-small"
                    density="comfortable"
                    class="text-medium-emphasis"
                    aria-label="About Auto visibility"
                  />
                </template>

                <v-card class="pa-3 text-body-2" max-width="400">
                  <div class="text-subtitle-2 mb-1">Auto visibility</div>
                  <div class="mb-3">
                    When visibility is set to Auto, the system decides website visibility from inventory status:
                  </div>

                  <div class="auto-visibility-groups mb-3">
                    <div
                      v-for="group in autoVisibilityGroups"
                      :key="group.key"
                      :data-testid="`auto-visibility-group-${group.key}`"
                    >
                      <div class="d-flex align-center ga-1">
                        <v-icon :icon="group.icon" size="16" :color="group.color" />
                        <span class="font-weight-medium">{{ group.title }}</span>
                      </div>
                      <ul class="auto-visibility-groups__list">
                        <li v-for="rule in group.rules" :key="rule.status">{{ rule.status }}</li>
                      </ul>
                    </div>
                  </div>

                  <div class="text-caption text-medium-emphasis">
                    Publish rules and required website data can also hide a vehicle.
                    Manual visibility overrides the automatic setting.
                  </div>

                  <v-divider class="my-3" />

                  <div data-testid="auto-visibility-mode-help">
                    <div class="mb-2">
                      <div class="font-weight-medium">{{ VISIBILITY_MODE_HELP.AUTO.title }}</div>
                      <div class="text-medium-emphasis">{{ VISIBILITY_MODE_HELP.AUTO.text }}</div>
                    </div>
                    <div>
                      <div class="font-weight-medium">{{ VISIBILITY_MODE_HELP.MANUAL.title }}</div>
                      <div class="text-medium-emphasis">{{ VISIBILITY_MODE_HELP.MANUAL.text }}</div>
                    </div>
                  </div>
                </v-card>
              </v-menu>
            </div>
          </template>

          <template #item.vehicle="{ item }">
            <InventoryVehicleIdentityCell
              :vehicle="item"
              :listing-indicator="getListingIndicator(item)"
              :listing-indicator-label="getListingIndicatorLabel(item)"
              :condition-label="formatConditionBadge(item.condition)"
            />
          </template>

          <template #item.visibility="{ item }">
            <InventoryVisibilityControls
              :vehicle="item"
              :toggling="togglingVins.has(item.vin)"
              :pending-action="togglingVins.get(item.vin) ?? null"
              :system-hidden="isAutomaticSystemHidden(item)"
              :explanation="formatWebsiteVisibilityExplanation(item)"
              :days-hidden="getDaysHidden(item.websiteVisibilityChangedAt)"
              :stale="isStaleHidden(item)"
              @request-toggle="(nextVisible: boolean) => requestVisibilityToggle(item, nextVisible)"
              @request-manual="requestManualPin(item)"
              @request-auto="requestAuto(item)"
            />
          </template>

          <template #item.stateUsage="{ item }">
            <div class="inventory-state-usage">
              <div class="text-body-2">
                <span class="d-sr-only">Inventory state: </span>{{ formatInventoryEnumLabel(item.inventoryState, inventoryStateOptions) }}
              </div>
              <div class="text-caption text-medium-emphasis">
                <span class="d-sr-only">Usage type: </span>{{ formatInventoryEnumLabel(item.inventoryUsageType, inventoryUsageTypeOptions) }}
              </div>
            </div>
          </template>

          <template #item.source="{ item }">
            <v-chip
              size="x-small"
              variant="tonal"
              :color="inventorySourceLabel(item) === 'Manual' ? 'deep-purple' : 'primary'"
            >
              {{ inventorySourceLabel(item) }}
            </v-chip>
          </template>

          <template #item.price="{ item }">
            <span
              v-if="pricingHubLaunch.inspectorUrlForVin(item.vin)"
              class="d-inline-flex align-center"
            >
              <span>{{ item.price != null ? formatPrice(item.price) : '—' }}</span>
              <PriceInspectorLink
                :href="pricingHubLaunch.inspectorUrlForVin(item.vin)!"
                :vin="item.vin"
                test-id="inventory-price-inspector-link"
              />
            </span>
            <span v-else>{{ item.price != null ? formatPrice(item.price) : '—' }}</span>
          </template>

          <template #item.days_in_inventory="{ item }">
            <v-menu open-on-hover open-on-click location="top">
              <template #activator="{ props }">
                <button
                  v-bind="props"
                  type="button"
                  class="days-trigger"
                  :aria-label="`${calculateDaysInInventoryFromVehicle(item)} days in inventory, show details`"
                  @click.stop
                >
                  {{ calculateDaysInInventoryFromVehicle(item) }}
                </button>
              </template>

              <v-card class="pa-3 text-body-2" max-width="320">
                {{ inventoryEntryDateText(item) }}
              </v-card>
            </v-menu>
          </template>

          <template #no-data>
            <div class="text-center pa-8">
              <v-icon icon="mdi-package-variant-closed" size="80" color="grey" class="mb-4" />
              <div class="text-h6 mb-2">No vehicles in inventory</div>
              <div class="text-body-2 text-grey mb-4">
                Start building your inventory by adding your first vehicle or importing a CSV feed.
              </div>
              <div class="d-flex justify-center ga-3">
                <v-btn color="primary" prepend-icon="mdi-plus" @click="navigateToAddVehicle">
                  Add Vehicle
                </v-btn>
                <v-btn variant="outlined" prepend-icon="mdi-upload" @click="handleImport">
                  Import
                </v-btn>
              </div>
            </div>
          </template>
        </v-data-table-server>

    <template #outside>
    <!-- Website visibility toggle feedback snackbar -->
    <v-snackbar
      v-model="toggleSnackbar.show"
      :color="toggleSnackbar.color"
      timeout="3000"
      location="bottom"
    >
      {{ toggleSnackbar.message }}
    </v-snackbar>

    <!-- Confirmation dialog for Hide/Show toggle and the Manual control pin -->
    <v-dialog v-model="visibilityConfirm.show" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-h6 pt-5 px-6">
          <template v-if="visibilityConfirm.kind === 'pin'">
            <v-icon color="info" class="mr-2">mdi-tune-variant</v-icon>
            Switch to Manual control?
          </template>
          <template v-else>
            <v-icon
              :color="visibilityConfirm.newValue ? 'success' : 'warning'"
              class="mr-2"
            >
              {{ visibilityConfirm.newValue ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}
            </v-icon>
            {{ visibilityConfirm.newValue ? 'Show Vehicle on Live Site?' : 'Hide Vehicle from Live Site?' }}
          </template>
        </v-card-title>
        <v-card-text class="px-6 pb-2 text-body-2 text-medium-emphasis">
          <template v-if="visibilityConfirm.kind === 'pin'">
            <p class="mb-4">
              <strong>{{ visibilityConfirm.vehicle?.vin }}</strong> stays
              <strong>{{ visibilityConfirm.newValue ? 'Visible' : 'Hidden' }}</strong> on the live website.
              Only control changes: automatic visibility will no longer update this vehicle.
            </p>
            <p v-if="visibilityConfirm.systemHidden && visibilityConfirm.vehicle" class="mb-4">
              Currently: {{ formatWebsiteVisibilityExplanation(visibilityConfirm.vehicle) }}
              Manual ownership replaces this automatic decision. The vehicle stays hidden and will not be shown.
            </p>
            <v-select
              v-if="!visibilityConfirm.newValue"
              v-model="visibilityConfirm.reason"
              :items="hideReasonOptions"
              item-title="label"
              item-value="value"
              label="Reason for keeping hidden *"
              hint="Select why this vehicle is hidden under manual control"
              persistent-hint
              variant="outlined"
              density="comfortable"
            />
          </template>
          <template v-else-if="visibilityConfirm.newValue">
            <p
              v-if="visibilityConfirm.kind === 'toggle' && visibilityConfirm.vehicle?.websiteVisibility === 'HIDDEN'"
              class="text-body-2 mb-3"
              data-testid="visibility-confirm-hidden-details"
            >
              Currently hidden: {{ hiddenDetailsFor(visibilityConfirm.vehicle) }}
            </p>
            This will make <strong>{{ visibilityConfirm.vehicle?.vin }}</strong> immediately visible on the live website feed. Shoppers will be able to see this vehicle right away.
          </template>
          <template v-else>
            <p class="mb-4">
              This will immediately hide <strong>{{ visibilityConfirm.vehicle?.vin }}</strong> from the live website feed. Shoppers will no longer see this vehicle.
            </p>
            <v-select
              v-model="visibilityConfirm.reason"
              :items="hideReasonOptions"
              item-title="label"
              item-value="value"
              label="Reason for hiding *"
              hint="Select why this vehicle is being hidden"
              persistent-hint
              variant="outlined"
              density="comfortable"
            />
          </template>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer />
          <v-btn variant="text" @click="visibilityConfirm.show = false">Cancel</v-btn>
          <v-btn
            :color="visibilityConfirm.kind === 'pin' ? 'info' : visibilityConfirm.newValue ? 'success' : 'warning'"
            variant="elevated"
            :disabled="!visibilityConfirm.newValue && !visibilityConfirm.reason"
            @click="confirmVisibilityToggle"
          >
            {{
              visibilityConfirm.kind === 'pin'
                ? 'Yes, switch to Manual'
                : visibilityConfirm.newValue
                  ? 'Yes, Show It'
                  : 'Yes, Hide It'
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Floating Add button (phones/tablets) -->
    <v-fab-transition>
      <v-btn
        v-if="mdAndDown"
        class="add-fab"
        color="primary"
        variant="elevated"
        size="large"
        prepend-icon="mdi-plus"
        @click="navigateToAddVehicle"
      >
        Add Vehicle
      </v-btn>
    </v-fab-transition>
    </template>
  </TopLevelPageShell>
</template>

<script lang="ts" setup>
import { formatWebsiteVisibilityExplanation } from '@/utils/websiteVisibilityExplanation'
import { computed, reactive, ref, watch, unref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import {
  INVENTORY_STATE,
  INVENTORY_USAGE_TYPE,
  WEBSITE_VISIBILITY,
  WEBSITE_VISIBILITY_MODE,
} from '@jazel-auto/ise-contracts/dist/inventory.js'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { usePricingHubLaunch } from '@/composables/usePricingHubLaunch'
import { useSegmentStore } from '@/stores/segment'
import { fetchIsePrimarySources } from '@/services/iseSettingsService'
import {
  fetchVehicleByVin,
  fetchVehicleDraftList,
  fetchInventory,
  returnWebsiteVisibilityToAuto,
  summarizeDraftsByIntent,
  toggleWebsiteVisibility,
  type DraftSummaryByIntent,
  type InventoryState,
  type InventoryUsageType,
  type InventoryVehicle,
  type WebsiteVisibility,
  type WebsiteVisibilityMode,
} from '@/services/inventory'
import { getInventoryListingIndicator, type ListingIndicator } from '@/utils/inventoryListingIndicator'
import { isValidTimeZone } from '@/utils'
import {
  assessManualPinPreflight,
  describeHiddenDetails,
  describeInventoryEntryDate,
  formatConditionBadge,
  formatInventoryEnumLabel,
  hiddenBadgeCause,
  inventorySourceLabel,
  isAutomaticSystemHidden,
  normalizeReason,
  VISIBILITY_MODE_HELP,
  type ManualPinSnapshot,
} from '@/utils/inventoryTableDisplay'
import InventorySearchToolbarRow from '@/components/InventorySearchToolbarRow.vue'
import TopLevelPageShell from '@/components/layout/TopLevelPageShell.vue'
import PriceInspectorLink from '@/components/inventory/PriceInspectorLink.vue'
import InventoryVehicleIdentityCell from '@/components/inventory/InventoryVehicleIdentityCell.vue'
import InventoryVisibilityControls from '@/components/inventory/InventoryVisibilityControls.vue'
import InventoryVehicleSortControls from '@/components/inventory/InventoryVehicleSortControls.vue'

const router = useRouter()
const route = useRoute()
const accountSettings = useAccountSettingsStore()
const pricingHubLaunch = usePricingHubLaunch()
const segmentStore = useSegmentStore()
const { mdAndDown } = useDisplay()

const isLoading = ref(false)
const draftSummaryByIntent = ref<DraftSummaryByIntent | null>(null)
const endpoint = import.meta.env.VITE_ISE_BACKEND_URL

const dealerTimeZone = computed(() => String(accountSettings.dealerTimeZone || '').trim())

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return count === 1 ? singular : plural
}

function joinHumanList(parts: string[]): string {
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0]
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`
}

const unpublishedSummaryText = computed(() => {
  const byIntent: DraftSummaryByIntent = draftSummaryByIntent.value ?? {
    CREATE: 0,
    PATCH: 0,
    DELETE: 0,
    RESTORE: 0,
  }

  const create = byIntent.CREATE
  const patch = byIntent.PATCH
  const del = byIntent.DELETE
  const restore = byIntent.RESTORE

  const total = create + patch + del + restore
  if (total === 0) return ''

  const parts: string[] = []

  if (create > 0) {
    parts.push(`${create} ${pluralize(create, 'new vehicle')}`)
  }

  if (patch > 0) {
    parts.push(`${patch} ${pluralize(patch, 'vehicle edit')}`)
  }

  if (del > 0) {
    parts.push(`${del} ${pluralize(del, 'archive change')}`)
  }

  if (restore > 0) {
    parts.push(`${restore} ${pluralize(restore, 'restore change')}`)
  }

  return `${total} ${pluralize(total, 'unpublished change')}: ${joinHumanList(parts)}.`
})

const autoVisibilityRules = [
  { status: 'On order', visibility: 'Visible' },
  { status: 'In production', visibility: 'Visible' },
  { status: 'In transit', visibility: 'Visible' },
  { status: 'Ready for sale', visibility: 'Visible' },
  { status: 'In service', visibility: 'Visible' },
  { status: 'Unknown', visibility: 'Hidden' },
  { status: 'Reserved', visibility: 'Visible' },
  { status: 'Sold', visibility: 'Hidden' },
] as const

const autoVisibilityGroups = [
  {
    key: 'visible',
    title: 'Visible by default',
    icon: 'mdi-eye-outline',
    color: 'success',
    rules: autoVisibilityRules.filter((r) => r.visibility === 'Visible'),
  },
  {
    key: 'hidden',
    title: 'Hidden by default',
    icon: 'mdi-eye-off-outline',
    color: 'warning',
    rules: autoVisibilityRules.filter((r) => r.visibility === 'Hidden'),
  },
]

// Six grouped columns (R1). Year / Make / Model sorting is driven from the Vehicle header
// controls through the table's native toggleSort, so the Vehicle column itself is not sortable.
const columnOptions = [
  { title: 'Vehicle', key: 'vehicle', sortable: false },
  {
    title: 'State / Usage',
    key: 'stateUsage',
    sortable: false,
    headerProps: { class: 'inventory-col--fit' },
    cellProps: { class: 'inventory-col--fit' },
  },
  {
    title: 'Source',
    key: 'source',
    sortable: false,
    headerProps: { class: 'inventory-col--fit' },
    cellProps: { class: 'inventory-col--fit' },
  },
  {
    title: 'Price',
    key: 'price',
    sortable: true,
    align: 'center' as const,
    headerProps: { class: 'inventory-col--fit' },
    cellProps: { class: 'inventory-col--fit inventory-col--numeric' },
  },
  {
    title: 'Days',
    key: 'days_in_inventory',
    sortable: true,
    align: 'center' as const,
    headerProps: { class: 'inventory-col--fit' },
    cellProps: { class: 'inventory-col--fit inventory-col--numeric' },
  },
  {
    title: 'Visibility',
    key: 'visibility',
    sortable: false,
    headerProps: { class: 'inventory-col--fit' },
    cellProps: { class: 'inventory-col--fit' },
  },  
]

const visibleColumns = ref(columnOptions.map((c) => c.key))
const computedHeaders = computed(() =>
  columnOptions.filter((c) => visibleColumns.value.includes(c.key)),
)

type GroupOption = { label: string; value: string; props?: { disabled?: boolean } }
const defaultGroupOptions: GroupOption[] = [
  { label: 'All vehicles', value: 'all' },
  { label: 'New', value: 'new' },
  { label: 'Used', value: 'used' },
  { label: 'CPO', value: 'cpo' },
]

// Inventory filtering supports server-side segment filtering via `/inventory?segment=...`.
const groupOptions = computed<GroupOption[]>(() => {
  const segs = unref(segmentStore.segments) || []
  const list = Array.isArray(segs) ? segs : []
  if (list.length === 0) return defaultGroupOptions

  const toOpt = (s: any): GroupOption => ({
    label: String(s?.name || s?.id || ''),
    value: String(s?.id || ''),
  })

  const all = list.find((s: any) => String(s?.id) === 'all')
  const rest = list.filter((s: any) => String(s?.id) !== 'all')
  const opts = [all ? toOpt(all) : defaultGroupOptions[0], ...rest.map(toOpt)].filter(
    (o) => o.label && o.value,
  )

  // Ensure at least the built-in groups exist even if dealer config is odd/missing.
  const ensure = (id: string, fallbackLabel: string) => {
    if (!opts.some((o) => o.value === id)) opts.push({ label: fallbackLabel, value: id })
  }
  ensure('new', 'New')
  ensure('used', 'Used')
  ensure('cpo', 'CPO')

  return opts
})

const sourceOptions = [
  { label: 'All sources', value: 'all' },
  { label: 'Feed', value: 'auto' },
  { label: 'Manual', value: 'manual' },
]

const separateReadyCourtesyVehicles = ref(true)

const inventoryStateOptions = computed<Array<{ label: string; value: 'all' | InventoryState }>>(() => [
  { label: 'All active', value: 'all' },
  { label: 'On order', value: INVENTORY_STATE.ON_ORDER },
  { label: 'In production', value: INVENTORY_STATE.IN_PRODUCTION },
  { label: 'In transit', value: INVENTORY_STATE.IN_TRANSIT },
  { label: 'Ready for sale', value: INVENTORY_STATE.READY_FOR_SALE },
  ...(separateReadyCourtesyVehicles.value
    ? [{ label: 'Ready Courtesy', value: INVENTORY_STATE.READY_COURTESY }]
    : []),
  { label: 'In service', value: INVENTORY_STATE.IN_SERVICE },
  { label: 'Unknown', value: INVENTORY_STATE.UNKNOWN },
  { label: 'Reserved', value: INVENTORY_STATE.RESERVED },
  { label: 'Sold', value: INVENTORY_STATE.SOLD },
])

const inventoryUsageTypeOptions: Array<{ label: string; value: 'all' | InventoryUsageType }> = [
  { label: 'All usage types', value: 'all' },
  { label: 'Retail', value: INVENTORY_USAGE_TYPE.RETAIL },
  { label: 'Wholesale', value: INVENTORY_USAGE_TYPE.WHOLESALE },
  { label: 'Demo', value: INVENTORY_USAGE_TYPE.DEMO },
  { label: 'Loaner', value: INVENTORY_USAGE_TYPE.LOANER },
  { label: 'Company vehicle', value: INVENTORY_USAGE_TYPE.COMPANY_VEHICLE },
  { label: 'Service', value: INVENTORY_USAGE_TYPE.SERVICE },
]

const websiteVisibilityOptions = [
  { label: 'All', value: 'all' },
  { label: 'Visible', value: WEBSITE_VISIBILITY.VISIBLE },
  { label: 'Hidden', value: WEBSITE_VISIBILITY.HIDDEN },
]

const searchQuery = ref('')
const feedTypeFilter = ref<'all' | 'manual' | 'auto'>('all')
const groupFilter = ref<string>('all')
const inventoryStateFilter = ref<'all' | InventoryState>('all')
const inventoryUsageTypeFilter = ref<'all' | InventoryUsageType>('all')
const websiteVisibilityFilter = ref<'all' | WebsiteVisibility>('all')
const tagFilter = ref<string | null>(null)

const USER_TAG_OPTIONS = [
  { label: 'Hot', value: 'HOT' },
  { label: 'Featured', value: 'FEATURED' },
  { label: 'Price Reduced', value: 'PRICE_REDUCED' },
  { label: 'New Arrival', value: 'NEW_ARRIVAL' },
  { label: 'Test Drive', value: 'TEST_DRIVE' },
  { label: 'Loaner', value: 'LOANER' },
  { label: 'Demo', value: 'DEMO' },
  { label: 'Wholesale', value: 'WHOLESALE' },
  { label: 'As-Is', value: 'AS_IS' },
  { label: 'Manager Special', value: 'MANAGER_SPECIAL' },
  { label: 'Certified', value: 'CERTIFIED' },
]

const SYSTEM_TAG_OPTIONS = [
  { label: '⚡ Recently Added', value: 'RECENTLY_ADDED' },
  { label: '⚡ Recently Sold', value: 'RECENTLY_SOLD' },
  { label: '⚡ Aged 30+', value: 'AGED_30_PLUS' },
  { label: '⚡ Aged 60+', value: 'AGED_60_PLUS' },
  { label: '⚡ Price Drop', value: 'PRICE_DROP' },
  { label: '⚡ Missing Photos', value: 'MISSING_PHOTOS' },
]

const allTagOptions = [
  { label: '— User Tags —', value: '__header_user', props: { disabled: true } },
  ...USER_TAG_OPTIONS,
  { label: '— System Tags —', value: '__header_system', props: { disabled: true } },
  ...SYSTEM_TAG_OPTIONS,
]

// Per-VIN pending visibility action: the key set is the pending guard; the value tells the row
// which control shows the loader (R15).
type VisibilityAction = 'toggle' | 'manual' | 'auto'
const togglingVins = ref<Map<string, VisibilityAction>>(new Map())
const markPending = (vin: string, action: VisibilityAction) => {
  togglingVins.value = new Map(togglingVins.value).set(vin, action)
}
const clearPending = (vin: string) => {
  const next = new Map(togglingVins.value)
  next.delete(vin)
  togglingVins.value = next
}
const toggleSnackbar = ref<{
  show: boolean
  message: string
  color: 'success' | 'error' | 'warning'
}>({
  show: false,
  message: '',
  color: 'success',
})

const visibilityConfirm = ref<{
  show: boolean
  vehicle: InventoryVehicle | null
  newValue: boolean
  reason: string | null
  kind: 'toggle' | 'pin'
  systemHidden: boolean
  /** Confirmed snapshot for the Manual pin preflight (R4.7); unset for `kind: 'toggle'`. */
  expected?: ManualPinSnapshot
}>({
  show: false,
  vehicle: null,
  newValue: true,
  reason: null,
  kind: 'toggle',
  systemHidden: false,
})

const hideReasonOptions = [
  { value: 'RECON',        label: 'In recon' },
  { value: 'PRICING',      label: 'Pricing issue' },
  { value: 'PHOTOS',       label: 'Missing photos' },
  { value: 'TEMP_HOLD',    label: 'Temporary hold' },
  { value: 'RESERVED',     label: 'Reserved / deposit taken' },
  { value: 'SOLD_PENDING', label: 'Sold pending delivery / funding' },
  { value: 'OTHER',        label: 'Other' },
]

const STALE_THRESHOLDS_DAYS: Record<string, number> = {
  RECON: 14,
  PRICING: 7,
  PHOTOS: 7,
  TEMP_HOLD: 14,
  RESERVED: 7,
  SOLD_PENDING: 3,
  OTHER: 14,
}

const getDaysHidden = (changedAt: string | undefined): number | null => {
  if (!changedAt) return null
  const diffMs = Date.now() - new Date(changedAt).getTime()
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  return days >= 0 ? days : null
}

const isStaleHidden = (item: InventoryVehicle): boolean => {
  if (item.websiteVisibility !== WEBSITE_VISIBILITY.HIDDEN) return false
  const days = getDaysHidden(item.websiteVisibilityChangedAt)
  if (days === null) return false
  const threshold = STALE_THRESHOLDS_DAYS[item.websiteVisibilityReason ?? ''] ?? 14
  return days >= threshold
}

const hiddenDetailsFor = (v: InventoryVehicle): string => {
  const explanation = formatWebsiteVisibilityExplanation(v)
  return describeHiddenDetails({
    explanation,
    // Only an evidence-supported cause is named (R16).
    reasonLabel: hiddenBadgeCause(explanation),
    daysHidden: getDaysHidden(v.websiteVisibilityChangedAt),
    stale: isStaleHidden(v),
  })
}

const isApplyingRouteFilters = ref(false)
const hasAppliedInitialRouteFilters = ref(false)

const applyRouteFilters = () => {
  // Supported deep-links:
  // - /inventory?segment=new|used|cpo|<segmentId>
  // - /inventory?type=manual|auto|all
  // - /inventory?inventoryState=READY_FOR_SALE|all
  // - /inventory?inventoryUsageType=RETAIL|WHOLESALE|all
  // - /inventory?websiteVisibility=VISIBLE|HIDDEN|all
  // - /inventory?q=<search>
  const q = route.query || {}

  const seg =
    typeof q.segment === 'string' && q.segment.trim().length > 0 ? q.segment.trim() : undefined
  const type =
    q.type === 'manual' || q.type === 'auto' || q.type === 'all' ? (q.type as any) : undefined
  const inventoryState =
    q.inventoryState === 'all' || inventoryStateOptions.value.some((o) => o.value === q.inventoryState)
      ? (q.inventoryState as 'all' | InventoryState)
      : undefined
  const inventoryUsageType =
    q.inventoryUsageType === 'all' || inventoryUsageTypeOptions.some((o) => o.value === q.inventoryUsageType)
      ? (q.inventoryUsageType as 'all' | InventoryUsageType)
      : undefined
  const wvFilter =
    q.websiteVisibility === WEBSITE_VISIBILITY.VISIBLE ||
    q.websiteVisibility === WEBSITE_VISIBILITY.HIDDEN ||
    q.websiteVisibility === 'all'
      ? (q.websiteVisibility as 'all' | WebsiteVisibility)
      : undefined
  const search = typeof q.q === 'string' ? q.q : undefined

  isApplyingRouteFilters.value = true
  try {
    if (seg != null) groupFilter.value = seg
    if (type != null) feedTypeFilter.value = type
    if (inventoryState != null) inventoryStateFilter.value = inventoryState
    if (inventoryUsageType != null) inventoryUsageTypeFilter.value = inventoryUsageType
    if (wvFilter != null) websiteVisibilityFilter.value = wvFilter
    if (search != null) searchQuery.value = search
  } finally {
    isApplyingRouteFilters.value = false
    hasAppliedInitialRouteFilters.value = true
  }
}

const tableOptions = reactive({
  page: 1,
  itemsPerPage: 50,
  sortBy: [] as Array<{ key: string; order: 'asc' | 'desc' }>,
})

const rawItems = ref<InventoryVehicle[]>([])
const totalItems = ref(0)

const tableItems = computed(() => rawItems.value)
const activeRequestId = ref(0)
const searchDebounce = ref<number | null>(null)
const hasAppliedInitialTableOptions = ref(false)

const SUPPORTED_SORT_KEYS = ['year', 'make', 'model', 'price', 'days_in_inventory'] as const
type SupportedSortKey = (typeof SUPPORTED_SORT_KEYS)[number]

const isSupportedSortKey = (key: string): key is SupportedSortKey =>
  (SUPPORTED_SORT_KEYS as readonly string[]).includes(key)

const sortOptionsEqual = (
  left: Array<{ key: string; order: 'asc' | 'desc' }>,
  right: Array<{ key: string; order: 'asc' | 'desc' }>,
) => JSON.stringify(left) === JSON.stringify(right)

const resolveSortQueryParams = (): Pick<
  Parameters<typeof fetchInventory>[1],
  'sortBy' | 'sortDirection'
> => {
  const activeSort = tableOptions.sortBy[0]
  if (!activeSort?.key || !isSupportedSortKey(activeSort.key)) return {}

  return {
    sortBy: activeSort.key,
    sortDirection: activeSort.order === 'desc' ? 'desc' : 'asc',
  }
}

const loadDraftSummary = async () => {
  if (!endpoint || !accountSettings.accountId) {
    draftSummaryByIntent.value = null
    return
  }

  try {
    const response = await fetchVehicleDraftList(
      endpoint,
      accountSettings.accountId,
      accountSettings.sessionToken,
    )
    draftSummaryByIntent.value = summarizeDraftsByIntent(response.drafts)
  } catch (e) {
    console.warn('Failed to load vehicle drafts for inventory banner (non-fatal):', e)
  }
}

const loadReadyCourtesySetting = async () => {
  const jazelId = String(accountSettings.accountId || '')
  if (!endpoint || !jazelId) return

  try {
    const settings = await fetchIsePrimarySources(endpoint, jazelId, {
      includeDraft: false,
      token: accountSettings.sessionToken,
    })
    if (String(accountSettings.accountId || '') !== jazelId) return

    separateReadyCourtesyVehicles.value = settings.separate_ready_courtesy_vehicles === true
    if (
      !separateReadyCourtesyVehicles.value &&
      inventoryStateFilter.value === INVENTORY_STATE.READY_COURTESY
    ) {
      inventoryStateFilter.value = 'all'
    }
  } catch (error) {
    // Keep the existing fail-open option list if settings cannot be loaded.
    console.warn('Failed to load published Ready Courtesy setting for Inventory filters:', error)
  }
}

const fetchData = async () => {
  if (!accountSettings.accountId) return

  void loadDraftSummary()

  const requestId = ++activeRequestId.value
  isLoading.value = true
  try {
    const useSearch = searchQuery.value.trim().length > 0
    const pageOffset = tableOptions.page - 1
    const offset = pageOffset * tableOptions.itemsPerPage
    const limit = tableOptions.itemsPerPage

    let vehicles: InventoryVehicle[] = []
    let totalFromServer: number | null = null

    const q = useSearch ? searchQuery.value.trim() : undefined
    const response = await fetchInventory(endpoint, {
      jazelId: accountSettings.accountId,
      limit,
      offset,
      type: feedTypeFilter.value === 'all' ? undefined : feedTypeFilter.value,
      // Group filter uses server-side `segment` matching for ALL groups (built-in and custom).
      segment: groupFilter.value === 'all' ? undefined : groupFilter.value,
      inventoryState: inventoryStateFilter.value,
      inventoryUsageType: inventoryUsageTypeFilter.value,
      includeArchived: false,
      q,
      websiteVisibility: websiteVisibilityFilter.value,
      tag: tagFilter.value ?? undefined,
      ...resolveSortQueryParams(),
    })

    vehicles = response.vehicles || []
    totalFromServer = response.pagination?.total ?? null

    if (requestId !== activeRequestId.value) {
      return
    }

    totalItems.value = typeof totalFromServer === 'number' ? totalFromServer : vehicles.length
    rawItems.value = vehicles
  } catch (error) {
    console.error('Failed to load inventory', error)
    rawItems.value = []
    totalItems.value = 0
  } finally {
    if (requestId === activeRequestId.value) {
      isLoading.value = false
    }
  }
}

const updateOptions = (options: {
  page: number
  itemsPerPage: number
  sortBy: Array<{ key: string; order: 'asc' | 'desc' }>
}) => {
  const changedPage = tableOptions.page !== options.page
  const changedItemsPerPage = tableOptions.itemsPerPage !== options.itemsPerPage
  const changedSortBy = !sortOptionsEqual(tableOptions.sortBy, options.sortBy ?? [])

  if (changedSortBy) {
    tableOptions.page = 1
  } else {
    tableOptions.page = options.page
  }

  tableOptions.itemsPerPage = options.itemsPerPage
  tableOptions.sortBy = options.sortBy ?? []

  if (!accountSettings.accountId) {
    return
  }

  if (!hasAppliedInitialTableOptions.value) {
    hasAppliedInitialTableOptions.value = true
    if (!changedPage && !changedItemsPerPage && !changedSortBy) {
      return
    }
  }

  if (changedPage || changedItemsPerPage || changedSortBy) {
    fetchData()
  }
}

const handleSearchInput = () => {
  tableOptions.page = 1
  if (searchDebounce.value) {
    window.clearTimeout(searchDebounce.value)
    searchDebounce.value = null
  }
  // Explicit search only: do not auto-fetch on typing
}

const triggerSearchNow = () => {
  tableOptions.page = 1
  if (searchDebounce.value) {
    window.clearTimeout(searchDebounce.value)
    searchDebounce.value = null
  }
  fetchData()
}

const clearFilters = () => {
  searchQuery.value = ''
  feedTypeFilter.value = 'all'
  groupFilter.value = 'all'
  inventoryStateFilter.value = 'all'
  inventoryUsageTypeFilter.value = 'all'
  websiteVisibilityFilter.value = 'all'
  tagFilter.value = null
  tableOptions.page = 1
  fetchData()
}

const hasActiveFilters = computed(() => {
  return (
    searchQuery.value.trim().length > 0 ||
    feedTypeFilter.value !== 'all' ||
    groupFilter.value !== 'all' ||
    inventoryStateFilter.value !== 'all' ||
    inventoryUsageTypeFilter.value !== 'all' ||
    websiteVisibilityFilter.value !== 'all' ||
    tagFilter.value !== null
  )
})

watch(feedTypeFilter, () => {
  if (isApplyingRouteFilters.value) return
  tableOptions.page = 1
  fetchData()
})

watch(groupFilter, () => {
  if (isApplyingRouteFilters.value) return
  tableOptions.page = 1
  fetchData()
})

watch(inventoryStateFilter, () => {
  if (isApplyingRouteFilters.value) return
  tableOptions.page = 1
  fetchData()
})

watch(inventoryUsageTypeFilter, () => {
  if (isApplyingRouteFilters.value) return
  tableOptions.page = 1
  fetchData()
})

watch(websiteVisibilityFilter, () => {
  if (isApplyingRouteFilters.value) return
  tableOptions.page = 1
  fetchData()
})

watch(tagFilter, () => {
  if (isApplyingRouteFilters.value) return
  tableOptions.page = 1
  fetchData()
})

const requestVisibilityToggle = (vehicle: InventoryVehicle, newValue: boolean) => {
  visibilityConfirm.value = {
    show: true,
    vehicle,
    newValue,
    reason: null,
    kind: 'toggle',
    systemHidden: false,
  }
}

// AUTO -> MANUAL keeps the current Visible/Hidden value; only ownership changes (D2, D7).
// Opens the confirmation dialog only: no request is made until the user confirms.
const requestManualPin = (vehicle: InventoryVehicle) => {
  if (vehicle.websiteVisibilityMode === WEBSITE_VISIBILITY_MODE.MANUAL) return
  if (
    vehicle.inventoryState === INVENTORY_STATE.ARCHIVED ||
    vehicle.membershipStatus === 'REMOVED'
  ) {
    return
  }
  if (togglingVins.value.has(vehicle.vin)) return

  const visible = vehicle.websiteVisibility !== WEBSITE_VISIBILITY.HIDDEN
  const systemHidden = isAutomaticSystemHidden(vehicle)
  visibilityConfirm.value = {
    show: true,
    vehicle,
    newValue: visible,
    reason: null,
    kind: 'pin',
    systemHidden,
    expected: {
      visibility: visible ? 'VISIBLE' : 'HIDDEN',
      mode: 'AUTO',
      reason: normalizeReason(vehicle.websiteVisibilityReason),
      systemHidden,
    },
  }
}

// MANUAL -> AUTO reuses Return to Auto directly (no confirmation). VBtn `loading` does not block
// programmatic clicks, so the pending guard lives here (I6).
const requestAuto = (vehicle: InventoryVehicle) => {
  if (vehicle.websiteVisibilityMode !== WEBSITE_VISIBILITY_MODE.MANUAL) return
  if (togglingVins.value.has(vehicle.vin)) return
  void handleReturnToAuto(vehicle)
}

const confirmVisibilityToggle = async () => {
  // The dialog closes synchronously below, so a second confirm click is a no-op (I6).
  if (!visibilityConfirm.value.show) return
  const { vehicle, newValue, reason, kind, expected } = visibilityConfirm.value
  visibilityConfirm.value.show = false
  if (!vehicle) return
  if (kind === 'pin') {
    if (expected) await handleManualPin(vehicle, reason ?? undefined, expected)
    return
  }
  await handleWebsiteVisibilityToggle(vehicle, newValue, reason ?? undefined)
}

const handleWebsiteVisibilityToggle = async (vehicle: InventoryVehicle, newValue: boolean, reason?: string) => {
  if (!accountSettings.accountId) return
  const vin = vehicle.vin
  markPending(vin, 'toggle')
  try {
    const nextWebsiteVisibility = newValue ? 'VISIBLE' : 'HIDDEN'
    await toggleWebsiteVisibility(
      endpoint,
      accountSettings.accountId,
      vin,
      nextWebsiteVisibility,
      reason,
      undefined,
      WEBSITE_VISIBILITY_MODE.MANUAL,
      accountSettings.sessionToken,
    )
    const now = new Date().toISOString()
    // Optimistic update in local list
    const idx = rawItems.value.findIndex((v) => v.vin === vin)
    if (idx !== -1) {
      rawItems.value = rawItems.value.map((v, i) =>
        i === idx ? {
          ...v,
          websiteVisibility: nextWebsiteVisibility,
          websiteVisibilityMode: WEBSITE_VISIBILITY_MODE.MANUAL,
          websiteVisibilityReason: newValue ? undefined : reason,
          websiteVisibilityChangedAt: now,
          // Drop the explanation cached at load time; it no longer describes this row (as pin / auto do).
          websiteVisibilityExplanation: undefined,
        } : v,
      )
    }
    toggleSnackbar.value = {
      show: true,
      message: newValue ? `${vin} is now visible on the website.` : `${vin} is now hidden from the website.`,
      color: 'success',
    }
  } catch (err) {
    toggleSnackbar.value = {
      show: true,
      message: `Failed to update website visibility: ${err instanceof Error ? err.message : String(err)}`,
      color: 'error',
    }
  } finally {
    clearPending(vin)
  }
}

// Manual pin (R4.4/R4.5/R4.7): preflight read -> compare with the confirmed snapshot -> one
// mutation. The VIN stays pending for the whole sequence and is released on every path.
const handleManualPin = async (
  vehicle: InventoryVehicle,
  reason: string | undefined,
  expected: ManualPinSnapshot,
) => {
  const accountId = accountSettings.accountId
  if (!accountId) return
  const vin = vehicle.vin
  if (togglingVins.value.has(vin)) return
  markPending(vin, 'manual')

  let stage: 'read' | 'write' = 'read'
  try {
    const resp = await fetchVehicleByVin(endpoint, accountId, vin)
    const assessment = assessManualPinPreflight(resp, expected)

    if (assessment.outcome === 'unverifiable') {
      toggleSnackbar.value = {
        show: true,
        message: `Could not verify the current visibility of ${vin}. No change was made.`,
        color: 'error',
      }
      return
    }

    if (assessment.outcome === 'changed') {
      rawItems.value = rawItems.value.map((v) =>
        v.vin === vin ? { ...v, ...assessment.refreshed } : v,
      )
      toggleSnackbar.value = {
        show: true,
        message: `${vin} changed since this list loaded. Review its current visibility and confirm again.`,
        color: 'warning',
      }
      return
    }

    const verified = assessment
    const hidden = verified.visibility === 'HIDDEN'
    stage = 'write'
    const res = await toggleWebsiteVisibility(
      endpoint,
      accountId,
      vin,
      verified.visibility,
      hidden ? reason : undefined,
      undefined,
      WEBSITE_VISIBILITY_MODE.MANUAL,
      accountSettings.sessionToken,
    )
    // Only after the API responds: ownership changes, visibility and changedAt stay as displayed (I7).
    rawItems.value = rawItems.value.map((v) =>
      v.vin === vin
        ? {
            ...v,
            websiteVisibility: verified.visibility,
            websiteVisibilityMode: WEBSITE_VISIBILITY_MODE.MANUAL,
            websiteVisibilityReason: hidden ? (res.reason ?? reason) : undefined,
            websiteVisibilityExplanation: undefined,
            websiteVisibilityChangedAt: v.websiteVisibilityChangedAt,
          }
        : v,
    )
    toggleSnackbar.value = {
      show: true,
      message: `${vin} is now under manual visibility control.`,
      color: 'success',
    }
  } catch (err) {
    toggleSnackbar.value = {
      show: true,
      message:
        stage === 'read'
          ? `Could not verify the current visibility of ${vin}. No change was made.`
          : `Failed to switch to manual visibility: ${err instanceof Error ? err.message : String(err)}`,
      color: 'error',
    }
  } finally {
    clearPending(vin)
  }
}

const handleReturnToAuto = async (vehicle: InventoryVehicle) => {
  if (!accountSettings.accountId) return
  const vin = vehicle.vin
  markPending(vin, 'auto')
  try {
    const res = await returnWebsiteVisibilityToAuto(endpoint, accountSettings.accountId, vin, undefined, accountSettings.sessionToken)
    const nextVisibility = res.websiteVisibility ?? vehicle.websiteVisibility
    rawItems.value = rawItems.value.map((v) =>
      v.vin === vin
        ? {
            ...v,
            websiteVisibility: nextVisibility,
            websiteVisibilityMode: WEBSITE_VISIBILITY_MODE.AUTO as WebsiteVisibilityMode,
            websiteVisibilityReason: res.reason ?? undefined,
            websiteVisibilityExplanation: undefined,
            websiteVisibilityChangedAt: res.changedAt ?? v.websiteVisibilityChangedAt,
          }
        : v,
    )
    toggleSnackbar.value = {
      show: true,
      message: `${vin} is now controlled by automatic visibility.`,
      color: 'success',
    }
  } catch (err) {
    toggleSnackbar.value = {
      show: true,
      message: `Failed to return visibility to automatic: ${err instanceof Error ? err.message : String(err)}`,
      color: 'error',
    }
  } finally {
    clearPending(vin)
  }
}

watch(
  () => route.query,
  () => {
    // Apply once initially, and also on back/forward navigation.
    applyRouteFilters()
    if (accountSettings.accountId) {
      tableOptions.page = 1
      fetchData()
    }
  },
  { immediate: true },
)

watch(
  () => accountSettings.accountId,
  (id) => {
    if (id) {
      void loadReadyCourtesySetting()
      // Load groups for the Group dropdown
      segmentStore.getSegments().catch(() => {
        // Non-blocking: fall back to default options if this fails
      })
      if (!hasAppliedInitialRouteFilters.value) {
        applyRouteFilters()
      }
      tableOptions.page = 1
      fetchData()
    }
  },
  { immediate: true },
)

const navigateToAddVehicle = () => {
  router.push('/inventory/add')
}

const navigateToArchivedInventory = () => {
  router.push('/inventory/archived')
}

const goToUnpublishedChanges = () => {
  router.push('/unpublished')
}

const handleRowClick = (_event: any, context: any) => {
  const vehicle: InventoryVehicle | undefined = context?.item?.raw ?? context?.item
  if (!vehicle) return

  router.push({
    name: 'VehicleDetails',
    params: { vin: vehicle.vin },
  })
}

const handleImport = () => {
  // future: open import flow
}

const formatPrice = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)

const getListingIndicator = (v: InventoryVehicle): ListingIndicator =>
  getInventoryListingIndicator(v as any)

const getListingIndicatorLabel = (v: InventoryVehicle): string => {
  const ind = getListingIndicator(v)
  if (ind === 'draft') return 'Draft (not published)'
  if (ind === 'listed') return 'Visible'
  if (ind === 'hidden') return 'Hidden'
  if (ind === 'archived') return 'Archived'
  return 'Status unknown'
}

const normalizeEpochSeconds = (timestamp: number | undefined): number | undefined => {
  if (!timestamp || typeof timestamp !== 'number' || timestamp <= 0) return undefined
  // Some feeds provide epoch milliseconds (13 digits). Normalize to seconds.
  return timestamp >= 1_000_000_000_000 ? Math.floor(timestamp / 1000) : timestamp
}

const getInventoryStartMs = (item: InventoryVehicle): number | undefined => {
  const sec = normalizeEpochSeconds(item.date_in_stock_ts)
  if (sec) return sec * 1000

  if (!item.addedDate) return undefined

  const addedMs = new Date(item.addedDate).getTime()
  return Number.isFinite(addedMs) && addedMs > 0 ? addedMs : undefined
}

const isArchivedInventoryItem = (item: InventoryVehicle): boolean => {
  return (
    item.inventoryState === 'ARCHIVED' ||
    item.membershipStatus === 'REMOVED'
  )
}

const getArchivedAt = (item: InventoryVehicle): string | undefined => {
  return item.archivedAt || item.removedAt || item.delistedDate
}

const getInventoryEndMs = (item: InventoryVehicle): number => {
  const archivedAt = getArchivedAt(item)
  if (isArchivedInventoryItem(item) && archivedAt) {
    const archivedMs = new Date(archivedAt).getTime()
    if (Number.isFinite(archivedMs) && archivedMs > 0) return archivedMs
  }

  return Date.now()
}

const inventoryEntryDateText = (item: InventoryVehicle): string =>
  describeInventoryEntryDate({
    startMs: getInventoryStartMs(item),
    fromStockDate: Boolean(normalizeEpochSeconds(item.date_in_stock_ts)),
    timeZone: isValidTimeZone(dealerTimeZone.value) ? dealerTimeZone.value : undefined,
  })

const calculateDaysInInventoryFromVehicle = (item: InventoryVehicle): string => {
  const startMs = getInventoryStartMs(item)
  if (!startMs) return '—'

  const endMs = getInventoryEndMs(item)
  if (!Number.isFinite(endMs) || endMs < startMs) return '—'

  const days = Math.floor((endMs - startMs) / (1000 * 60 * 60 * 24))
  return days >= 0 ? String(days) : '—'
}

</script>

<style scoped>
/* The shared shell's v-card-subtitle does not wrap; let this page's subtitle wrap so it never widens the page. */
.inventory-subtitle {
  white-space: normal;
}

.control-bar {
  background-color: #f8fafc;
  color: #111827;
  border: 1px solid #e5e7eb;
}

.add-fab {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 2000;
}

.v-data-table th {
  font-weight: bold;
  background-color: #f5f5f5;
}

.inventory-table {
  /* Keep the table from stretching the page: the table scrolls inside its own wrapper instead. */
  contain: inline-size;
}

.inventory-table :deep(td) {
  white-space: normal;
  vertical-align: middle;
}

.inventory-table :deep(.inventory-col--fit) {
  white-space: nowrap;
}

.inventory-table :deep(.inventory-col--numeric) {
  font-variant-numeric: tabular-nums;
}

.inventory-table :deep(.v-data-table__td),
.inventory-table :deep(.v-data-table__th) {
  padding: 6px 12px;
}

.inventory-state-usage > div {
  white-space: nowrap;
}

.auto-visibility-groups {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px 16px;
}

.auto-visibility-groups__list {
  list-style: none;
  padding: 0;
  margin: 4px 0 0 22px;
}

.days-trigger {
  min-width: 32px;
  padding: 2px 0 2px 6px;
  text-align: end;
  border-radius: 4px;
  text-decoration: underline dotted;
  cursor: pointer;
}

.days-trigger:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 1px;
}
</style>
